'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export interface FAQ {
  id: string;
  question: string;
  answer: string;
  category?: string;
}

export default function FaqSection() {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL || '/api'}/faq`);
        if (!response.ok) {
          throw new Error('Failed to fetch FAQs');
        }
        const data = await response.json();
        setFaqs(data);
      } catch (error) {
        console.error('Error fetching FAQs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFAQs();
  }, []);

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // Filter FAQs related to the adoption process
  const processFaqs = faqs.filter(faq => 
    faq.category === 'process' || 
    faq.question.toLowerCase().includes('process') ||
    faq.question.toLowerCase().includes('timeline') ||
    faq.question.toLowerCase().includes('adoption')
  );

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto mt-12">
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg animate-pulse">
              <div className="h-6 bg-warm-brown/10 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-warm-brown/5 rounded w-full"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (processFaqs.length === 0) {
    return null; // Don't render anything if no process-related FAQs found
  }

  return (
    <div className="max-w-3xl mx-auto mt-12">
      <h2 className="text-3xl font-bold text-warm-brown mb-8 text-center font-heading">
        Common Questions About Our Process
      </h2>
      <div className="space-y-4">
        {processFaqs.map((faq) => (
          <motion.div 
            key={faq.id}
            className="bg-white/90 backdrop-blur-sm rounded-2xl overflow-hidden shadow-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <button
              className="w-full text-left p-6 focus:outline-none flex justify-between items-center"
              onClick={() => toggleExpand(faq.id)}
              aria-expanded={expandedId === faq.id}
              aria-controls={`faq-${faq.id}`}
            >
              <h3 className="font-semibold text-lg text-warm-brown">
                {faq.question}
              </h3>
              <motion.span
                animate={{ rotate: expandedId === faq.id ? 180 : 0 }}
                className="text-soft-gold text-xl ml-4"
              >
                ▼
              </motion.span>
            </button>
            
            <AnimatePresence>
              {expandedId === faq.id && (
                <motion.div
                  id={`faq-${faq.id}`}
                  initial="collapsed"
                  animate="open"
                  exit="collapsed"
                  variants={{
                    open: { opacity: 1, height: 'auto' },
                    collapsed: { opacity: 0, height: 0 }
                  }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-6 pt-2 text-warm-brown/75">
                    <p className="whitespace-pre-line">{faq.answer}</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>
      <div className="mt-8 text-center">
        <a 
          href="/faq" 
          className="inline-flex items-center text-soft-gold hover:text-warm-brown transition-colors"
        >
          View all FAQs
          <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </a>
      </div>
    </div>
  );
}
